import { useEffect, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  CheckCircle2,
  Download,
  ExternalLink,
  Inbox,
  RefreshCw,
  Rss,
  Settings,
} from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useSession } from '@/lib/auth';
import {
  type MgrInboxItem,
  type UpdateMgrSourceInput,
  mgrErrorMessage,
  mgrInboxQueryOptions,
  mgrSourceQueryOptions,
  useImportMgrItem,
  useRefreshMgrFeed,
  useUpdateMgrSource,
} from '@/lib/feed-mgronline';
import { hasPermission } from '@/lib/permissions';

export const Route = createFileRoute('/_authenticated/feed-sources/')({
  component: FeedInboxPage,
});

function FeedInboxPage() {
  const { user } = useSession();
  const canManage = hasPermission(user, 'feed.manage');
  const query = useQuery(mgrInboxQueryOptions);
  const refresh = useRefreshMgrFeed();
  const importItem = useImportMgrItem();
  const navigate = useNavigate();
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  if (!canManage) {
    return <div className="p-8 text-center text-sm text-slate-500">ไม่มีสิทธิ์เข้าถึง feed</div>;
  }

  const handleImport = (item: MgrInboxItem) => {
    setPendingId(item.id);
    importItem.mutate(item.id, {
      onSuccess: (result) => {
        setPendingId(null);
        void navigate({ to: '/posts/$postId', params: { postId: result.postId } });
      },
      onError: () => setPendingId(null),
    });
  };

  const items = query.data ?? [];
  const newCount = items.filter((i) => i.status === 'new').length;

  return (
    <div className="p-4 sm:p-8">
      <div className="mx-auto max-w-5xl space-y-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-50">
              <Rss className="text-brand-600 h-6 w-6" />
              Feed Inbox
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              วัตถุดิบจาก Manager Online ({newCount} รอ import จาก {items.length} รายการ)
            </p>
            <p className="mt-0.5 text-[11px] text-slate-400">
              อัปเดตอัตโนมัติทุก 5 นาที · เก็บไว้ 24 ชั่วโมงแล้วลบ (รายการที่ import แล้วเก็บไว้ต่อ)
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setSettingsOpen(true)}>
              <Settings className="mr-1.5 h-3.5 w-3.5" />
              ตั้งค่า
            </Button>
            <Button
              variant="outline"
              onClick={() => refresh.mutate()}
              disabled={refresh.isPending || query.isFetching}
            >
              <RefreshCw
                className={`mr-2 h-4 w-4 ${refresh.isPending || query.isFetching ? 'animate-spin' : ''}`}
              />
              ดึงใหม่
            </Button>
          </div>
        </div>

        {(query.isError || importItem.isError) && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{mgrErrorMessage(query.error ?? importItem.error)}</AlertDescription>
          </Alert>
        )}

        {query.isLoading ? (
          <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-sm text-slate-500 dark:border-slate-800 dark:bg-slate-900">
            กำลังโหลด...
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-3 rounded-lg border border-slate-200 bg-white p-12 text-center dark:border-slate-800 dark:bg-slate-900">
            <Inbox className="h-12 w-12 text-slate-300" />
            <p className="text-sm text-slate-500">ยังไม่มีรายการใน feed</p>
            <p className="text-xs text-slate-400">
              รอ cron poll ครั้งถัดไป (ทุก 5 นาที) หรือกด "ดึงใหม่"
            </p>
          </div>
        ) : (
          <ul className="grid grid-cols-1 gap-3">
            {items.map((item) => (
              <FeedRow
                key={item.id}
                item={item}
                pending={pendingId === item.id}
                onImport={() => handleImport(item)}
                onOpenExisting={() =>
                  item.importedPostId &&
                  void navigate({
                    to: '/posts/$postId',
                    params: { postId: item.importedPostId },
                  })
                }
              />
            ))}
          </ul>
        )}
      </div>

      <MgrSourceSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
    </div>
  );
}

interface RowProps {
  item: MgrInboxItem;
  pending: boolean;
  onImport: () => void;
  onOpenExisting: () => void;
}

function FeedRow({ item, pending, onImport, onOpenExisting }: RowProps) {
  const dt = item.publishedAt
    ? new Date(item.publishedAt).toLocaleString('th-TH', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';
  return (
    <li className="flex gap-3 rounded-lg border border-slate-200 bg-white p-3 transition hover:border-slate-300 dark:border-slate-800 dark:bg-slate-900">
      <img
        src={item.thumbUrl || item.imageUrl}
        alt=""
        loading="lazy"
        className="h-20 w-28 shrink-0 rounded object-cover"
        // Some thumbs 404 — fall back to a transparent placeholder so the
        // row layout doesn't break.
        onError={(e) => {
          (e.target as HTMLImageElement).style.visibility = 'hidden';
        }}
      />
      <div className="min-w-0 flex-1">
        <div className="line-clamp-2 text-sm font-medium text-slate-900 dark:text-slate-50">
          {item.topic}
        </div>
        <div className="mt-1 line-clamp-2 text-xs text-slate-500">{item.intro}</div>
        <div className="mt-2 flex items-center justify-between text-[11px] text-slate-400">
          <span>{dt}</span>
          {item.status === 'imported' ? (
            <button
              type="button"
              onClick={onOpenExisting}
              className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 font-medium text-emerald-800 hover:bg-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300"
            >
              <CheckCircle2 className="h-3 w-3" />
              นำเข้าแล้ว · เปิดบทความ <ExternalLink className="h-3 w-3" />
            </button>
          ) : (
            <Button size="sm" onClick={onImport} disabled={pending}>
              {pending ? (
                <>
                  <RefreshCw className="mr-1.5 h-3.5 w-3.5 animate-spin" /> กำลังนำเข้า
                </>
              ) : (
                <>
                  <Download className="mr-1.5 h-3.5 w-3.5" /> นำเข้าเป็นร่าง
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </li>
  );
}

/* ---------- Settings dialog ---------- */

interface SettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function MgrSourceSettingsDialog({ open, onOpenChange }: SettingsDialogProps) {
  // Source row + detail URL drive the form prefill; only fetched while the
  // dialog is open so closing/reopening always shows the freshest server-side
  // values.
  const sourceQuery = useQuery({ ...mgrSourceQueryOptions, enabled: open });
  const update = useUpdateMgrSource();

  const [url, setUrl] = useState('');
  const [detailUrl, setDetailUrl] = useState('');
  const [name, setName] = useState('');
  const [interval, setInterval] = useState('10');
  const [isActive, setIsActive] = useState(true);
  const [submitError, setSubmitError] = useState<unknown>(null);

  // Reset form fields whenever a fresh source row arrives.
  useEffect(() => {
    if (sourceQuery.data) {
      const { source, detailUrl: serverDetailUrl } = sourceQuery.data;
      setUrl(source.url);
      setDetailUrl(serverDetailUrl);
      setName(source.name);
      setInterval(String(source.fetchIntervalMinutes));
      setIsActive(source.isActive);
      setSubmitError(null);
    }
  }, [sourceQuery.data]);

  const handleSave = () => {
    if (!sourceQuery.data) return;
    setSubmitError(null);
    const { source: original, detailUrl: originalDetailUrl } = sourceQuery.data;
    const patch: UpdateMgrSourceInput = {};
    const trimmedUrl = url.trim();
    const trimmedDetailUrl = detailUrl.trim();
    const trimmedName = name.trim();
    const parsedInterval = Number(interval);
    if (trimmedUrl && trimmedUrl !== original.url) patch.url = trimmedUrl;
    if (trimmedDetailUrl && trimmedDetailUrl !== originalDetailUrl) {
      patch.detailUrl = trimmedDetailUrl;
    }
    if (trimmedName && trimmedName !== original.name) patch.name = trimmedName;
    if (
      Number.isFinite(parsedInterval) &&
      parsedInterval !== original.fetchIntervalMinutes &&
      parsedInterval >= 5 &&
      parsedInterval <= 1440
    ) {
      patch.fetchIntervalMinutes = parsedInterval;
    }
    if (isActive !== original.isActive) patch.isActive = isActive;

    if (Object.keys(patch).length === 0) {
      onOpenChange(false);
      return;
    }

    update.mutate(patch, {
      onSuccess: () => onOpenChange(false),
      onError: (e) => setSubmitError(e),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>ตั้งค่า Manager Online feed</DialogTitle>
          <DialogDescription>
            URL ต้องคืน JSON shape ของ mgronline (<code>{'{ news: [...] }'}</code>) — ถ้า shape
            ไม่ตรงจะเห็น error ใน "ข้อผิดพลาดล่าสุด"
          </DialogDescription>
        </DialogHeader>

        {sourceQuery.isLoading ? (
          <div className="py-8 text-center text-sm text-slate-500">กำลังโหลด...</div>
        ) : sourceQuery.isError ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{mgrErrorMessage(sourceQuery.error)}</AlertDescription>
          </Alert>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="mgr-url">URL feed list (q.php)</Label>
              <Input
                id="mgr-url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://mjson.mgronline.com/q.php"
                spellCheck={false}
                autoComplete="off"
              />
              <p className="text-[11px] text-slate-400">
                คืน JSON ของรายการข่าวล่าสุด ใช้สำหรับ cron poll + รายการในหน้านี้
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="mgr-detail-url">URL รายละเอียดข่าว (c.php)</Label>
              <Input
                id="mgr-detail-url"
                value={detailUrl}
                onChange={(e) => setDetailUrl(e.target.value)}
                placeholder="https://mjson.mgronline.com/c.php"
                spellCheck={false}
                autoComplete="off"
              />
              <p className="text-[11px] text-slate-400">
                ใช้ตอนกด "นำเข้าเป็นร่าง" — ดึง body HTML + ผู้เขียน + รูปภาพทั้งหมด
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="mgr-name">ชื่อ</Label>
              <Input
                id="mgr-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={100}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="mgr-interval">รอบ poll (นาที)</Label>
                <Input
                  id="mgr-interval"
                  type="number"
                  min={5}
                  max={1440}
                  value={interval}
                  onChange={(e) => setInterval(e.target.value)}
                />
                <p className="text-[11px] text-slate-400">5 - 1440 นาที</p>
              </div>
              <div className="space-y-1.5">
                <Label className="block">สถานะ</Label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    className="h-4 w-4"
                  />
                  เปิดใช้งาน (cron poll)
                </label>
              </div>
            </div>

            {sourceQuery.data && (
              <div className="rounded-md bg-slate-50 p-3 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                <div>
                  Poll ล่าสุด:{' '}
                  {sourceQuery.data.source.lastPolledAt
                    ? new Date(sourceQuery.data.source.lastPolledAt).toLocaleString('th-TH')
                    : '—'}
                </div>
                {sourceQuery.data.source.lastError && (
                  <div className="mt-1 text-rose-600 dark:text-rose-400">
                    ข้อผิดพลาดล่าสุด: {sourceQuery.data.source.lastError}
                  </div>
                )}
              </div>
            )}

            {submitError !== null && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{mgrErrorMessage(submitError)}</AlertDescription>
              </Alert>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={update.isPending}>
            ยกเลิก
          </Button>
          <Button
            onClick={handleSave}
            disabled={update.isPending || sourceQuery.isLoading || sourceQuery.isError}
          >
            {update.isPending ? (
              <>
                <RefreshCw className="mr-1.5 h-3.5 w-3.5 animate-spin" /> กำลังบันทึก
              </>
            ) : (
              'บันทึก'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
